import {Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {catchError, Observable, of, Subscription, switchMap, take, tap, zip} from 'rxjs';
import {NativeServiceApiProvider} from 'src/app/services/native-api-provider.service';
import {ThemeService} from 'src/app/services/theme.service';
import {ProfileService} from 'src/app/auth_profile/services/profile.service';
import {OffcanvasMenuService} from 'src/app/services/offcanvas-menu.service';
import {AuthorizationServiceProvider} from 'src/app/auth_profile/services/authorization-service.provider';
import {LogsService} from 'src/app/utils/services/logs.service';
import {AuthorizedUserDetailsService, UserRole} from 'src/app/auth_profile/services/api/authorization.api';
import {Router} from '@angular/router';
import {PersonUtils} from "../../auth_profile/utils/v3/person-utils";

@Component({
  selector: 'app-top-bar-user-menu',
  templateUrl: './top-bar-user-menu.component.html',
  styleUrl: './top-bar-user-menu.component.scss',
  standalone: false
})
export class TopBarUserMenuComponent implements OnInit, OnDestroy {

  schoolId: number
  userRole: UserRole
  UserRole = UserRole
  profileFullName: string
  schoolName: string
  isImplemented = false;
  theme: string
  menuType: string
  offcanvasMenuSubscription: Subscription;

  @ViewChild('profileDropdown', { static: true }) profileDropdown!: ElementRef;
  profileDropdownOpen = false;
  @ViewChild('notificationsDropdown', { static: true }) notificationsDropdown!: ElementRef;
  notificationsDropdownOpen = false;

  profilePhoto: string | null = null;
  profileLink: string

  constructor(
    private router: Router,
    private authService: AuthorizationServiceProvider,
    private profileService: ProfileService,
    public provider: NativeServiceApiProvider,
    public themeService: ThemeService,
    private offcanvasMenuService: OffcanvasMenuService,
    public logs: LogsService,
  ){
    this.authService.getAuthDetailsService().pipe(
      switchMap(auth => zip(auth.getUserRole(), this.getSelfSchoolId(auth))),
      tap(([role, schoolId]) => {
        this.userRole = role
        this.schoolId = schoolId
      })
    ).subscribe()
  }

  ngOnInit(): void {
    this.provider.casa().pipe(
      switchMap(api => {
        if (api.isImplemented()) {
          this.isImplemented = true;
          if ([UserRole.Student, UserRole.Teacher, UserRole.Unaffiliated].includes(this.userRole))
            return this.loadProfileData()
          else
            return of(null);
        } else {
          return this.loadAppData();
        }
      })
    ).subscribe()

    this.themeService.getTheme().subscribe(theme => this.theme = theme)

    this.offcanvasMenuSubscription = this.offcanvasMenuService.getCurrentMenu()
      .subscribe(menuType => this.menuType = menuType)

    this.getProfileLink();
  }

  ngOnDestroy() {
    if(this.offcanvasMenuSubscription) this.offcanvasMenuSubscription.unsubscribe()
  }

  private loadProfileData() {
    return this.profileService.getSelfPerson().pipe(
      take(1),
      catchError(err => {
        this.logs.error("getSelfPerson failed", err);
        return of(null);
      }),
      tap(self => {
        if (self?.details) {
          this.profileFullName = PersonUtils.getPersonName(self.details)
        }
      }),
      switchMap(() => this.profileService.getSelfSchool().pipe(
        catchError(err => {
          this.logs.error("getSelfSchool failed", err);
          return of(null);
        }),
        tap(school => {
          if (school?.details?.name) {
            this.schoolName = school.details.name
          }
        })
      )),
      switchMap(() => this.profileService.getSelfPhoto().pipe(
        catchError(err => {
          this.logs.error("getSelfPhoto failed", err);
          return of(null);
        }),
        tap(photo => {
          if (photo?.publicAccessUrl) {
            this.profilePhoto = photo.publicAccessUrl
          }
        })
      ))
    )
  }

  private loadAppData() {
    return this.authService.getAuthDetailsService().pipe(
      switchMap(api => api.getUserName()),
      tap(userName => {
        this.profileFullName = userName
        this.schoolName = ""
      })
    )
  }

  getFullName(){
    return this.profileFullName
  }

  getRole() {
    if( this.userRole === UserRole.Student) {
      return this.schoolName? `${this.schoolName} student` : ''
    } else if (this.userRole === UserRole.Teacher) {
      return this.schoolName? `${this.schoolName} teacher` : ''
    }
    else return ''
  }

  getProfileLink() {
    const firstSegment = this.router.url.split('/')[1]
    this.profileLink = `/${firstSegment}/profile/profile-form`
  }

  onLogout() {
    this.authService.getCommonRoutines().subscribe(service => service.logout())
  }

  getAvatar() {
    return {background: this.profilePhoto? `url('${this.profilePhoto}')` : null}
  }

  private getSelfSchoolId(auth: AuthorizedUserDetailsService): Observable<any> {
    try {
      return auth.getSelfSchoolId().pipe(
        switchMap(_ => auth.getSelfSchoolId())
      )
    } catch (e) {
        return of(null)
    }
  }

  toggleProfileDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.profileDropdownOpen = !this.profileDropdownOpen;
    this.notificationsDropdownOpen = false;
  }

  toggleNotificationsDropdown(event: MouseEvent) {
    event.stopPropagation();
    this.notificationsDropdownOpen = !this.notificationsDropdownOpen;
    this.profileDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (
      this.profileDropdownOpen &&
      !this.profileDropdown.nativeElement.contains(event.target) ||
      this.notificationsDropdownOpen &&
      !this.notificationsDropdown.nativeElement.contains(event.target)
    ) {
      this.profileDropdownOpen = false;
      this.notificationsDropdownOpen = false;
    }
  }
}
